import { FormEvent, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { App, Category, Initiative, Priority, Status } from '../lib/types';
import { CATEGORIES, CATEGORY_LABEL, PRIORITIES, STATUSES, STATUS_LABEL } from '../lib/types';
import { deleteInitiative } from '../hooks/useRoadmap';

interface Props {
  apps: App[];
  initialAppId: string;
  initiative?: Initiative;
  onClose: () => void;
  onSaved: () => void;
}

export default function InitiativeModal({ apps, initialAppId, initiative, onClose, onSaved }: Props) {
  const [appId, setAppId] = useState(initiative?.app_id ?? initialAppId);
  const [title, setTitle] = useState(initiative?.title ?? '');
  const [description, setDescription] = useState(initiative?.description ?? '');
  const [category, setCategory] = useState<Category>(initiative?.category ?? 'feature');
  const [status, setStatus] = useState<Status>(initiative?.status ?? 'backlog');
  const [priority, setPriority] = useState<Priority>(initiative?.priority ?? 'P2');
  const [owner, setOwner] = useState(initiative?.owner ?? '');
  const [blockers, setBlockers] = useState(initiative?.blockers ?? '');
  const [startDate, setStartDate] = useState(initiative?.start_date ?? '');
  const [targetDate, setTargetDate] = useState(initiative?.target_date ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const payload = {
      app_id: appId,
      title: title.trim(),
      description: description.trim() || null,
      category,
      status,
      priority,
      owner: owner.trim() || null,
      blockers: blockers.trim() || null,
      start_date: startDate || null,
      target_date: targetDate || null,
      completed_at:
        status === 'done'
          ? initiative?.completed_at ?? new Date().toISOString()
          : null,
    };
    try {
      if (initiative?.id) {
        const { error } = await supabase
          .from('roadmap_initiatives')
          .update(payload)
          .eq('id', initiative.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('roadmap_initiatives').insert(payload);
        if (error) throw error;
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setSaving(false);
    }
  }

  async function onDelete() {
    if (!initiative?.id) return;
    if (!confirm(`Eliminar "${initiative.title}"?`)) return;
    setSaving(true);
    try {
      await deleteInitiative(initiative.id);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al eliminar');
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="glass rounded-2xl w-full max-w-xl p-6 space-y-4 max-h-[90vh] overflow-auto"
      >
        <div className="flex items-center gap-3">
          <h3 className="text-base font-semibold">
            {initiative ? 'Editar iniciativa' : 'Nueva iniciativa'}
          </h3>
          <button type="button" className="ml-auto btn text-xs" onClick={onClose}>✕</button>
        </div>

        <div>
          <label className="label">App</label>
          <select className="input" value={appId} onChange={(e) => setAppId(e.target.value)} required>
            {apps.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="label">Título</label>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus />
        </div>

        <div>
          <label className="label">Descripción</label>
          <textarea
            className="input min-h-[72px]"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="label">Categoría</label>
            <select className="input" value={category} onChange={(e) => setCategory(e.target.value as Category)}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Estado</label>
            <select className="input" value={status} onChange={(e) => setStatus(e.target.value as Status)}>
              {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Prioridad</label>
            <select className="input" value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Owner</label>
            <input className="input" value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="Ej: Julián + CC" />
          </div>
          <div>
            <label className="label">Blockers</label>
            <input className="input" value={blockers} onChange={(e) => setBlockers(e.target.value)} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Start</label>
            <input type="date" className="input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <label className="label">Target</label>
            <input type="date" className="input" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
          </div>
        </div>

        {error && (
          <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-md p-2">
            {error}
          </div>
        )}

        <div className="flex items-center gap-2">
          {initiative && (
            <button type="button" className="btn text-xs text-rose-300" onClick={onDelete} disabled={saving}>
              Eliminar
            </button>
          )}
          <button type="button" className="btn ml-auto" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
}
